import { Form } from 'factorial-form'
import { CustomFieldsFieldTypeEnum } from 'generated/resources/datatable.graphql'
import {
  ContractFlowCustomFieldFragment,
  ContractFlowCustomFieldValueFragment,
} from 'generated/resources/graphql'

export const getCustomFieldKey = (customField: ContractFlowCustomFieldFragment) => {
  return `cf-${customField.identifier}`
}

const customFieldTypeToFormType = (type: CustomFieldsFieldTypeEnum) => {
  switch (type) {
    case 'text':
    case 'long_text':
    case 'single_choice':
      return 'string'
    default:
      return type
  }
}

export const customFieldsFormKeys = (
  customFields: ContractFlowCustomFieldFragment[],
  customValues?: ContractFlowCustomFieldValueFragment[]
) => {
  const formKeys: { [key: string]: string } = {}

  return customFields.reduce((keys, field) => {
    const key = getCustomFieldKey(field)
    let formType = customFieldTypeToFormType(field.fieldType)

    // Defensive validation: if we have values, check if the value is stored in longTextValue
    // but the fieldType suggests a numeric type. This can happen when fieldType is incorrect.
    if (customValues) {
      const customValue = customValues.find((value) => {
        return value.field.id === field.id || value.field?.labelText === field.labelText
      })

      if (customValue) {
        // If value is in longTextValue but fieldType suggests a number/cents type,
        // treat it as string to avoid type mismatches
        if (customValue.longTextValue && (formType === 'number' || formType === 'cents')) {
          formType = 'string'
        }
      }
    }

    keys[key] = formType
    return keys
  }, formKeys)
}

const getCustomFieldValue = (customValue: ContractFlowCustomFieldValueFragment) => {
  const customField = customValue.field
  switch (customField.fieldType) {
    case 'text':
    case 'long_text':
      // Always return as string, even if the value looks like a number
      // This ensures text fields are never converted to numbers
      return customValue.longTextValue ?? undefined
    case 'single_choice':
      return customValue.singleChoiceValue
    case 'cents':
      // Convert null to undefined for cents fields (factorial-form doesn't accept null for numeric fields)
      return customValue.centsValue ? Number(customValue.centsValue) : undefined
    case 'date':
      return customValue.dateValue
  }

  return undefined
}

export const customFieldsToFormValues = (
  customFields: ContractFlowCustomFieldFragment[],
  customValues: ContractFlowCustomFieldValueFragment[]
) => {
  return customFields.reduce((formValues: { [key: string]: string | number }, field) => {
    const customFieldKey = getCustomFieldKey(field)
    let customValue = customValues.find((value) => {
      return value.field.id === field.id
    })

    // This is a workaround to handle the case where the custom field is not in the contract version values
    // because the field id is not the same as the field label text. It happens due to the duplications that we have at database level.
    // We need to tackle the root cause of this issue and then remove this workaround.
    if (!customValue) {
      customValue = customValues.find((value) => {
        return value.field?.labelText === field.labelText
      })
    }

    if (customValue) {
      // Use the field's declared type (from customFields) rather than the matched value's field type
      // to avoid type mismatches when using the labelText fallback matching
      const value = getCustomFieldValue({
        ...customValue,
        field: {
          ...customValue.field,
          fieldType: field.fieldType, // Override with the declared field type
        },
      })
      // Only add non-empty, non-undefined values
      if (value !== undefined && value !== null && value !== '') {
        formValues[customFieldKey] = value
      }
    }
    return formValues
  }, {})
}

export const getEditedCustomFields = (form: Form, fields: ContractFlowCustomFieldFragment[]) => {
  return fields.reduce((modifiedFields: { [key: string]: string }[], field) => {
    const key = getCustomFieldKey(field)

    if (!form.has(key)) {
      return modifiedFields
    }

    const cf_field = form.get(key)
    if (cf_field.isDirty) {
      modifiedFields.push({
        identifier: field.identifier,
        value: cf_field._mapOut()?.toString(),
      })
    }
    return modifiedFields
  }, [])
}
